import React from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Check, Calendar, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
export function PricesPage() {
  const {
    t,
    lang
  } = useTranslation();
  const consultationItems = [{
    service: t('prices.consultation'),
    price: t('prices.consultationPrice')
  }, {
    service: t('prices.examination'),
    price: t('prices.examinationPrice')
  }, {
    service: t('prices.xray'),
    price: t('prices.xrayPrice')
  }, {
    service: t('prices.emergencyAppointment'),
    price: t('prices.emergencyAppointmentPrice')
  }];
  const hygieneItems = [{
    service: t('prices.hygiene'),
    price: t('prices.hygienePrice')
  }, {
    service: t('prices.hygieneAirflow'),
    price: t('prices.hygieneAirflowPrice')
  }, {
    service: t('prices.whitening'),
    price: t('prices.whiteningPrice')
  }];
  const treatmentItems = [{
    service: t('prices.filling'),
    price: t('prices.fillingPrice')
  }, {
    service: t('prices.rootCanal'),
    price: t('prices.rootCanalPrice')
  }, {
    service: t('prices.extraction'),
    price: t('prices.extractionPrice')
  }, {
    service: t('prices.wisdomTooth'),
    price: t('prices.wisdomToothPrice')
  }];
  const restorationItems = [{
    service: t('prices.crown'),
    price: t('prices.crownPrice')
  }, {
    service: t('prices.veneer'),
    price: t('prices.veneerPrice')
  }, {
    service: t('prices.compositeBonding'),
    price: t('prices.compositeBondingPrice')
  }, {
    service: t('prices.bridge'),
    price: t('prices.bridgePrice')
  }];
  const prostheticsItems = [{
    service: t('prices.implant'),
    price: t('prices.implantPrice')
  }, {
    service: t('prices.denture'),
    price: t('prices.denturePrice')
  }, {
    service: t('prices.fullDenture'),
    price: t('prices.fullDenturePrice')
  }];
  const sectionTitles = {
    consultation: {
      en: 'Consultations & Diagnostics',
      ua: 'Консультації та діагностика',
      ru: 'Консультации и диагностика'
    },
    hygiene: {
      en: 'Hygiene & Whitening',
      ua: 'Гігієна та відбілювання',
      ru: 'Гигиена и отбеливание'
    },
    treatment: {
      en: 'General Treatment',
      ua: 'Загальне лікування',
      ru: 'Общее лечение'
    },
    restoration: {
      en: 'Restorations & Cosmetic',
      ua: 'Реставрації та естетика',
      ru: 'Реставрации и эстетика'
    },
    prosthetics: {
      en: 'Implants & Prosthetics',
      ua: 'Імпланти та протезування',
      ru: 'Импланты и протезирование'
    }
  };
  const PriceSection = ({
    title,
    items
  }: {
    title: string;
    items: {
      service: string;
      price: string;
    }[];
  }) => <div className="mb-8" data-id="element-285">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 px-2" data-id="element-286">{title}</h3>
      <Card className="overflow-hidden" data-id="element-287">
        <div className="divide-y divide-gray-100" data-id="element-288">
          {items.map((item, idx) => <div key={idx} className="flex justify-between items-center p-5 hover:bg-gray-50 transition-colors" data-id="element-289">
              <div className="flex items-center gap-3" data-id="element-290">
                <Check className="w-4 h-4 text-teal-600 flex-shrink-0" data-id="element-291" />
                <span className="text-gray-900" data-id="element-292">{item.service}</span>
              </div>
              <span className="text-lg font-semibold text-gray-900 whitespace-nowrap ml-4" data-id="element-293">
                {item.price}
              </span>
            </div>)}
        </div>
      </Card>
    </div>;
  return <Layout data-id="element-294">
      {/* Hero */}
      <section className="bg-teal-900 py-20 text-white" data-id="element-295">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-296">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-297">
            {t('prices.title')}
          </h1>
          <p className="text-xl text-teal-100 max-w-2xl mx-auto" data-id="element-298">
            {t('prices.subtitle')}
          </p>
        </div>
      </section>

      {/* Price List */}
      <section className="py-16 bg-gray-50" data-id="element-299">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-300">
          <PriceSection title={sectionTitles.consultation[lang]} items={consultationItems} data-id="element-301" />

          <PriceSection title={sectionTitles.hygiene[lang]} items={hygieneItems} data-id="element-302" />

          <PriceSection title={sectionTitles.treatment[lang]} items={treatmentItems} data-id="element-303" />

          <PriceSection title={sectionTitles.restoration[lang]} items={restorationItems} data-id="element-304" />

          <PriceSection title={sectionTitles.prosthetics[lang]} items={prostheticsItems} data-id="element-305" />

          {/* Note */}
          <p className="mt-8 text-center text-gray-600 text-sm" data-id="element-306">
            {t('prices.note')}
          </p>

          {/* CTA Buttons */}
          <div className="mt-12 flex flex-col sm:flex-row justify-center gap-4" data-id="element-307">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-308">
              <Button size="lg" className="gap-2 w-full sm:w-auto" data-id="element-309">
                <Calendar className="w-5 h-5" data-id="element-310" />
                {t('prices.bookCta')}
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-311">
              <Button variant="outline" size="lg" className="gap-2 w-full sm:w-auto" data-id="element-312">
                <MessageCircle className="w-5 h-5 text-green-600" data-id="element-313" />
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Services Link */}
      <section className="py-16 bg-white" data-id="element-314">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-315">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-316">
            {t('services.title')}
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-317">{t('services.subtitle')}</p>
          <Link to={`/${lang}/services`} data-id="element-318">
            <Button variant="secondary" size="lg" data-id="element-319">
              {t('services.viewAll')}
            </Button>
          </Link>
        </div>
      </section>
    </Layout>;
}